import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreVertical, FileDown, Copy, Eye, Mail, FileCode, Printer, Archive, ArchiveRestore, Info, FileCheck2 } from "lucide-react";
import { XmlUfficiale } from "@/components/XmlUfficiale";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg, download, TIPI } from "@/lib/api";
import { mailTo } from "@/components/MailButton";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const EMPTY_FILTRI = { q: "", tipo: "all", dal: "", al: "", numero: "", causale: "", saldata: "all" };

const F = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1 text-xs font-medium text-slate-600 ${className}`}>{label}{children}</label>
);

export const Filtri = ({ f, set, out, onReset }) => (
  <div className="mb-4 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-12" data-testid="invoice-filters">
    <F label="Ricerca" className="lg:col-span-3"><Input data-testid="invoice-search-input" placeholder={out ? "Cliente, P.IVA o codice univoco" : "Fornitore o P.IVA"} value={f.q} onChange={(e) => set({ ...f, q: e.target.value })} /></F>
    <F label="Tipo doc." className="lg:col-span-2">
      <Select value={f.tipo} onValueChange={(v) => set({ ...f, tipo: v })}>
        <SelectTrigger data-testid="invoice-filter-tipo"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Tutti</SelectItem>{Object.entries(TIPI).map(([k, v]) => <SelectItem key={k} value={k}>{k} · {v}</SelectItem>)}</SelectContent>
      </Select>
    </F>
    <F label="Dal" className="lg:col-span-2"><DateField clearable testid="invoice-filter-dal" value={f.dal} onChange={(v) => set({ ...f, dal: v })} /></F>
    <F label="Al" className="lg:col-span-2"><DateField clearable testid="invoice-filter-al" value={f.al} onChange={(v) => set({ ...f, al: v })} /></F>
    <F label="N. doc." className="lg:col-span-1"><Input data-testid="invoice-filter-numero" placeholder="n. doc" value={f.numero} onChange={(e) => set({ ...f, numero: e.target.value })} /></F>
    <F label="Saldate" className="lg:col-span-2">
      <Select value={f.saldata} onValueChange={(v) => set({ ...f, saldata: v })}>
        <SelectTrigger data-testid="invoice-filter-saldata"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="si">Saldate</SelectItem><SelectItem value="no">Da saldare</SelectItem></SelectContent>
      </Select>
    </F>
    <F label="Causale" className="lg:col-span-9"><Input data-testid="invoice-filter-causale" placeholder="Causale" value={f.causale} onChange={(e) => set({ ...f, causale: e.target.value })} /></F>
    <div className="flex items-end lg:col-span-3"><Button variant="outline" className="w-full bg-slate-800 text-white hover:bg-slate-700 hover:text-white" data-testid="invoice-filter-reset" onClick={onReset}>Azzera filtri</Button></div>
  </div>
);

export const filtriParams = (f) => ({
  q: f.q || undefined, tipo: f.tipo === "all" ? undefined : f.tipo, dal: f.dal || undefined, al: f.al || undefined,
  numero: f.numero || undefined, causale: f.causale || undefined, saldata: f.saldata === "all" ? undefined : f.saldata === "si",
});

const Item = ({ icon: I, children, ...p }) => <DropdownMenuItem className="cursor-pointer gap-2" {...p}><I className="h-4 w-4 text-slate-500" />{children}</DropdownMenuItem>;

export const RowMenu = ({ i, out, write, base, reload }) => {
  const nav = useNavigate();
  const dl = (p) => download(p).catch((e) => toast.error(e.message));
  const arch = async (azione) => { try { await api.post("/invoices/bulk", { ids: [i.id], azione }); toast.success(azione === "archivia" ? "Spostata nello storico" : "Ripristinata"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const creaRef = useRef(null);
  return (
    <>
    {out && write && !i.imported && <XmlUfficiale id={i.id} trigger={(crea) => { creaRef.current = crea; return null; }} />}
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="rounded-full border border-slate-300" data-testid={`invoice-menu-${i.id}`} onClick={(e) => e.stopPropagation()}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs font-normal text-slate-500"><Info className="mr-1 inline h-3 w-3" />ID SdI: {i.sdi_id || "non ancora inviata"}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <Item icon={Eye} onClick={() => nav(`${base}/${i.id}`)}>Apri dettaglio e notifiche SdI</Item>
        {out && write && <Item icon={Mail} data-testid={`invoice-menu-email-${i.id}`} onClick={() => mailTo(`/invoices/${i.id}/email`, i.controparte?.email, "la copia della fattura")}>Inviare email</Item>}
        <Item icon={Printer} onClick={() => dl(`/invoices/${i.id}/pdf`)}>Stampa PDF</Item>
        <Item icon={FileCode} data-testid={`invoice-menu-xml-${i.id}`} onClick={() => dl(`/invoices/${i.id}/xml`)}>Scarica file XML</Item>
        {out && write && !i.imported && <Item icon={FileCheck2} data-testid={`invoice-menu-xmlok-${i.id}`} onClick={() => creaRef.current?.()}>Crea XML ufficiale (controllato)</Item>}
        {write && (i.archiviata
          ? <Item icon={ArchiveRestore} data-testid={`invoice-menu-restore-${i.id}`} onClick={() => arch("ripristina")}>Riporta tra i documenti dell'anno</Item>
          : <Item icon={Archive} data-testid={`invoice-menu-archive-${i.id}`} onClick={() => arch("archivia")}>Sposta nello storico</Item>)}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
};

const Anteprima = ({ i, onClose }) => {
  const [url, setUrl] = useState(null);
  const [xml, setXml] = useState(null);
  const [tab, setTab] = useState("pdf");
  const showXml = () => { setTab("xml"); if (xml === null) api.get(`/invoices/${i.id}/xml`, { responseType: "text" }).then((r) => setXml(r.data)).catch((e) => toast.error(errMsg(e))); };
  useEffect(() => {
    let u;
    api.get(`/invoices/${i.id}/pdf`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setUrl(u); }).catch((e) => toast.error(errMsg(e)));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [i.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="h-[92vh] w-[95vw] max-w-5xl grid-rows-[auto_1fr]" data-testid="invoice-preview-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-3">Fattura {i.numero} del {i.data?.split("-").reverse().join("/")}
          <span className="flex rounded-lg border border-sky-200 bg-sky-50 p-1 text-sm font-medium">
            <button type="button" data-testid="preview-tab-pdf" onClick={() => setTab("pdf")} className={`rounded-md px-3 py-1 ${tab === "pdf" ? "bg-sky-600 text-white" : "text-sky-900"}`}>Fattura</button>
            <button type="button" data-testid="preview-tab-xml" onClick={showXml} className={`rounded-md px-3 py-1 ${tab === "xml" ? "bg-sky-600 text-white" : "text-sky-900"}`}>File XML</button>
          </span></DialogTitle></DialogHeader>
        {tab === "xml" ? <pre className="min-w-0 overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-emerald-100" data-testid="invoice-preview-xml">{xml ?? "Caricamento…"}</pre> :
        url ? <iframe title="Anteprima fattura" src={url} className="h-full w-full rounded border" data-testid="invoice-preview-frame" /> : <p className="text-sm text-slate-500">Caricamento…</p>}
      </DialogContent>
    </Dialog>
  );
};

export const RowIcons = ({ i, out, write }) => {
  const nav = useNavigate();
  const [view, setView] = useState(false);
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const B = ({ icon: I, title, onClick, id, cls = "" }) => <Button variant="ghost" size="icon" title={title} data-testid={id} className={`h-8 w-8 rounded-full border border-slate-400 bg-white ${cls}`} onClick={stop(onClick)}><I className="h-4 w-4" /></Button>;
  return (
    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
      <B icon={FileDown} title="Scarica PDF" id={`invoice-pdf-${i.id}`} cls="text-rose-700" onClick={() => download(`/invoices/${i.id}/pdf`).catch((e) => toast.error(e.message))} />
      {out && write && <B icon={Copy} title="Duplica fattura" id={`invoice-copy-${i.id}`} onClick={() => nav(`/app/fatture/nuova?copia=${i.id}`)} />}
      <B icon={Eye} title="Vedi fattura" id={`invoice-view-${i.id}`} cls="text-sky-700" onClick={() => setView(true)} />
      {view && <Anteprima i={i} onClose={() => setView(false)} />}
    </div>
  );
};

export const BulkBar = ({ ids, clear, reload, archived }) => {
  const run = async (azione, msg) => { try { await api.post("/invoices/bulk", { ids, azione }); toast.success(msg); clear(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const zip = async () => {
    try {
      const r = await api.post("/invoices/zip", { ids }, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "fatture_xml.zip"; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!ids.length) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2" data-testid="invoice-bulk-bar">
      <span className="mr-2 text-sm font-semibold text-sky-900">{ids.length} selezionate</span>
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-paid" onClick={() => run("saldata", "Segnate come saldate")}>Segna saldate</Button>
      <Button size="sm" variant="outline" className="bg-white" data-testid="bulk-unpaid" onClick={() => run("non_saldata", "Segnate da saldare")}>Segna da saldare</Button>
      <Button size="sm" variant="outline" className="bg-white" data-testid="bulk-zip" onClick={zip}><FileCode className="mr-1 h-4 w-4" />Scarica XML (ZIP)</Button>
      <Button size="sm" variant="outline" className="bg-white" data-testid="bulk-archive" onClick={() => run(archived ? "ripristina" : "archivia", archived ? "Ripristinate" : "Spostate nello storico")}>{archived ? "Riporta nell'anno" : "Sposta nello storico"}</Button>
      <ConfirmDelete testid="bulk-delete" label="Elimina selezionate" title={`Eliminare ${ids.length} documenti?`}
        text="Vengono eliminati solo i documenti importati da XML, le fatture ricevute, le bozze e gli invii simulati. Quelli inviati davvero allo SdI restano. L'operazione non può essere annullata."
        onConfirm={async () => {
          try {
            const { data } = await api.post("/invoices/bulk", { ids, azione: "elimina" });
            toast.success(`${data.eliminate} eliminati${data.saltate ? `, ${data.saltate} non eliminabili (inviati allo SdI)` : ""}`);
            clear(); reload();
          } catch (e) { toast.error(errMsg(e)); }
        }} />
      <Button size="sm" variant="ghost" data-testid="bulk-clear" onClick={clear}>Annulla selezione</Button>
    </div>
  );
};

export const Pager = ({ total, page, setPage, size, setSize }) => {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <Select value={String(size)} onValueChange={(v) => { setSize(Number(v)); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white" data-testid="invoice-page-size"><SelectValue /></SelectTrigger>
          <SelectContent>{[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n} righe/pagina</SelectItem>)}</SelectContent>
        </Select>
        <span data-testid="invoice-count">Totali: <b>{total}</b></span>
      </div>
      <div className="flex items-center gap-1 text-sm text-slate-600">
        <span className="mr-2">Pagina {page} di {pages}:</span>
        {Array.from({ length: pages }, (_, n) => n + 1).slice(Math.max(0, page - 4), page + 3).map((n) => (
          <button key={n} type="button" data-testid={`invoice-page-${n}`} onClick={() => setPage(n)} className={`h-8 min-w-8 rounded border px-2 ${n === page ? "border-sky-600 bg-sky-600 text-white" : "border-slate-200 bg-white hover:bg-sky-50"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
};
